/**
 * Custom Edge Component
 * Styled connection between nodes
 */

import React, { useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useNodes,
} from 'reactflow';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { useDesignerModeStore } from '@flow-studio/store'; // import { designerModeStore';
import { EdgeEditModal } from '../Modals/EdgeEditModal';
import { PencilIcon } from '@bizfirst/common-react';
import type { WorkflowNodeData } from '@flow-studio/types';

export const CustomEdge: React.FC<EdgeProps & { type?: string }> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  markerStart,
  label,
  labelStyle,
  labelShowBg,
  animated,
  selected,
  data,
  source,
  target,
  type,
}) => {
  const { handleEdgeContextMenu } = useContextMenu();
  const { deleteEdge } = useWorkflowStore();
  const { theme } = useUIStore();
  const { setSelectedEdges, setSelectedNodes, setSelectedHandle } = useUIStore();

  // Get all nodes to check if edge connects to/from satellite nodes
  const nodes = useNodes<WorkflowNodeData>();
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);
  const isSatelliteEdge = sourceNode?.data?.isSatellite || targetNode?.data?.isSatellite;

  // Get execution state from store
  const nodeExecutionStates = useDesignerModeStore((s) => s.nodeExecutionStates);
  const mode = useDesignerModeStore((s) => s.mode);

  // Get source and target node execution states
  const sourceExecutionState = nodeExecutionStates.get(source);
  const targetExecutionState = nodeExecutionStates.get(target);

  // Determine if edge is active (execution is flowing through it)
  // Edge is active if:
  // 1. We're in execution mode AND
  // 2. Source node has completed/success AND target node is running
  const isEdgeActive =
    mode !== 'design' &&
    sourceExecutionState?.status === 'success' &&
    targetExecutionState?.status === 'running';
  const [showEditModal, setShowEditModal] = useState(false);

  const handleEdgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEdges([id]);
    setSelectedNodes([]);
    setSelectedHandle(null);
  };

  // Determine path based on edge type (type prop wins over legacy data.edgeType)
  const edgeType = type || data?.edgeType || 'smoothstep';
  let edgePath, labelX, labelY;

  if (edgeType === 'straight') {
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  } else if (edgeType === 'smoothstep' || edgeType === 'step') {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  } else {
    [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  }

  const isAnimated = animated && !isEdgeActive;
  const edgeStyle: React.CSSProperties = {
    ...style,
    stroke: isEdgeActive ? '#3b82f6' : (selected ? '#3b82f6' : (style.stroke || '#9ca3af')),
    strokeWidth: isEdgeActive ? 3 : (selected ? 3 : (style.strokeWidth || 2)),
    strokeDasharray: isEdgeActive ? '8, 4' : (isSatelliteEdge ? '4, 4' : (isAnimated ? '6, 3' : 'none')),
    animation: isEdgeActive ? 'edge-flow 1.5s linear infinite' : (isAnimated ? 'edge-flow 2s linear infinite' : 'none'),
    opacity: isEdgeActive ? 1 : (style.opacity || 0.8),
  };

  const markerColor = isEdgeActive ? '#3b82f6' : (selected ? '#3b82f6' : (typeof style.stroke === 'string' ? style.stroke : '#9ca3af'));
  const markerStyle: string = data?.markerStyle || 'arrow';

  const renderEndMarker = () => {
    if (markerStyle === 'arrow') return null; // ReactFlow's built-in markerEnd handles this
    const size = 7;
    switch (markerStyle) {
      case 'circle':
        return <circle cx={targetX} cy={targetY} r={size / 2} fill={markerColor} />;
      case 'square':
        return <rect x={targetX - size / 2} y={targetY - size / 2} width={size} height={size} fill={markerColor} />;
      case 'diamond':
        return (
          <polygon
            points={`${targetX},${targetY - size} ${targetX + size},${targetY} ${targetX},${targetY + size} ${targetX - size},${targetY}`}
            fill={markerColor}
          />
        );
      default:
        return null;
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    deleteEdge(id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowEditModal(true);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent edge selection on first click
  };

  return (
    <>
      {/* Invisible wider path for easier clicking */}
      <path
        d={edgePath}
        className="edge-interaction-path"
        onClick={handleEdgeClick}
      />

      {/* Visible edge */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerStyle === 'arrow' ? markerEnd : undefined}
        markerStart={markerStart}
        style={edgeStyle}
      />

      {/* Custom endpoint marker (circle / square / diamond) */}
      {renderEndMarker()}

      <EdgeLabelRenderer>
        {/* Label */}
        {label && (
          <div
            className={`edge-label ${theme === 'light' ? 'light' : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              ...(labelShowBg === false ? { background: 'none', border: 'none' } : {}),
              ...labelStyle,
            }}
            onContextMenu={(e) => handleEdgeContextMenu(e, id, data)}
          >
            {label}
          </div>
        )}

        {/* Action Buttons (visible when selected) */}
        {selected && (
          <div
            className="edge-button-container nodrag nopan"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            {/* Edit Button */}
            <button
              className={`edge-button edit ${theme === 'light' ? 'light' : ''}`}
              onClick={handleEdit}
              onMouseDown={handleMouseDown}
              title="Edit connection"
            >
              <PencilIcon size={9} />
            </button>

            {/* Delete Button */}
            <button
              className={`edge-button delete ${theme === 'light' ? 'light' : ''}`}
              onClick={handleDelete}
              onMouseDown={handleMouseDown}
              title="Delete connection"
            >
              ×
            </button>
          </div>
        )}
      </EdgeLabelRenderer>

      {/* Edit Modal */}
      {showEditModal && (
        <EdgeEditModal
          edgeId={id}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};
